/**
 * EVENT BINDINGS — Centralized event delegation for admin panel
 * Replaces all inline onclick/onchange/oninput/onsubmit/onkeyup handlers.
 */
import { App } from './admin.js';
import { setIdiomaAdmin, siguienteIdiomaAdmin } from './services/i18n-admin.js';
import { TabbedModal } from './components/tabbed-modal.js';
import { LotowheelModule } from './modules/games/lotowheel/lotowheel.js';
import { LotowheelSimulator } from './modules/games/lotowheel/lotowheel-simulator.js';
import { PlatformModule } from './modules/platform.js';
import { proxyLazy, proxyLazyFuncion } from './services/lazy-modules.js';
const PlatformNodos = proxyLazy('platformNodos');
const FinanzasModule = proxyLazy('finanzas');
const FinanzasB2BModule = proxyLazy('finanzasB2b');
const PlatformDispositivos = proxyLazy('platformDispositivos');
const ClientesModule = proxyLazy('clientes');
const Digi12Module = proxyLazy('digi12');
const MarketingModule = proxyLazy('marketing');
const SistemaModule = proxyLazy('sistema');
const PlatformAdministradores = proxyLazy('platformAdministradores');
const TicketsModule = proxyLazy('tickets');
const PozosModule = proxyLazy('pozos');
const ActividadModule = proxyLazy('actividad');
const MarketingCuponesModule = proxyLazy('marketingCupones');
const MarketingTicketsModule = proxyLazy('marketingTickets');
const abrirModalDeRecargaDeSaldoPrepago = proxyLazyFuncion('platformSaldosRed', 'abrirModalDeRecargaDeSaldoPrepago');
const cargarMasNodosDeSaldosPrepago = proxyLazyFuncion('platformSaldosRed', 'cargarMasNodosDeSaldosPrepago');
const revisarRecargaPrepago = proxyLazyFuncion('platformSaldosRed', 'revisarRecargaPrepago');
const atrasRecargaPrepago = proxyLazyFuncion('platformSaldosRed', 'atrasRecargaPrepago');
const confirmarRecargaPrepago = proxyLazyFuncion('platformSaldosRed', 'confirmarRecargaPrepago');
const cancelarRecargaPrepago = proxyLazyFuncion('platformSaldosRed', 'cancelarRecargaPrepago');
const pagarDelegadoTicket = proxyLazyFuncion('platformSaldosRed', 'pagarDelegadoTicket');
const filtrarPorChipDeNivel = proxyLazyFuncion('platformSaldosRed', 'filtrarPorChipDeNivel');
const verBilhetesDelNodo = proxyLazyFuncion('platformSaldosRed', 'verBilhetesDelNodo');
import { PlatformFichaDispositivos } from './modules/platform-ficha-dispositivos.js';
import { StatsModal } from './components/stats-modal.js';
import { NodeStatisticsDashboard } from './components/node-statistics-professional-dashboard.js';
import { ContratoModal } from './components/contrato-modal.js';
import { SetupWizard } from './components/setup-wizard-engine.js';
import { ConfirmacionDestructiva } from './components/confirmacion-destructiva.js';
import { PlatformFichaFinanzasNodo } from './modules/platform-ficha-finanzas-nodo.js';

// ---------------------------------------------------------------------------
// CLICK actions map
// ---------------------------------------------------------------------------
const CLICK_HANDLERS = {
    // --- App global ---
    'app:logout': () => App.logout(),
    'app:cambiarIdiomaAdmin': () => setIdiomaAdmin(siguienteIdiomaAdmin().codigo),
    'app:toggleSidebar': () => App.toggleSidebar(),
    'app:cerrarSidebar': () => App.cerrarSidebar(),
    'app:toggleAlertas': () => App.toggleAlertas(),
    'app:openAlertasConfig': () => App.openAlertasConfig(),
    'app:closeModal': () => App.closeModal(),
    'app:closeModalGeneric': () => App.closeModal('modalGeneric'),
    'confirmDestructiva:cancelar': () => ConfirmacionDestructiva.cancelar(),
    'confirmDestructiva:aceptar': () => ConfirmacionDestructiva.aceptar(),
    'app:saveAlertasConfig': () => App.saveAlertasConfig(),
    'app:copyStatsToClipboard': () => App.copyStatsToClipboard(),
    'app:copyGenericContentToClipboard': () => App.copyGenericContentToClipboard(),
    'app:switchGameTab': el => App.switchTab('juegos', el.dataset.tab),
    'app:toggleSorteo': el => App.toggleSorteo(Number(el.dataset.id)),
    'app:openSorteoModal': el => App.openSorteoModal(Number(el.dataset.id)),
    'app:handleAlertaClick': el =>
        App.handleAlertaClick(el.dataset.targetTipo, Number(el.dataset.targetId), el.dataset.tipo),
    // Tarjetas "Acción necesaria" del dashboard operativo scoped (no-superadmin)
    'app:ccOperativoNavegar': el => App.ccOperativoNavegar(el.dataset.destino),

    // App.toggleSorteoFilter(param)
    'app:toggleSorteoFilter': el => App.toggleSorteoFilter(el.dataset.param),

    // App.switchLotowheelSubtab(param)
    'app:switchLotowheelSubtab': el => App.switchLotowheelSubtab(el.dataset.param),

    // App.switchTab(view, tab)
    'app:switchTab': el => App.switchTab(el.dataset.view, el.dataset.tab),

    // --- LotoWheel ---
    'lotowheel:openConfigModal': el =>
        LotowheelModule.openLotowheelConfigModal(
            el.dataset.id ? Number(el.dataset.id) : undefined,
            el.dataset.nodoId || null
        ),
    'lotowheel:selectTheme': el => LotowheelModule.selectTheme(el.dataset.theme),
    'lotowheel:deleteBloqueo': el => LotowheelModule.deleteBloqueo(Number(el.dataset.id)),
    'lotowheel:removeChip': el => LotowheelModule.removeChip(Number(el.dataset.index)),
    'lotowheel:triggerVirtualDraw': el => LotowheelModule.triggerVirtualDraw(el.dataset.id),
    'lotowheel:stopVirtualDraw': el => LotowheelModule.stopVirtualDraw(el.dataset.id),
    'lotowheel:selectPresetFromDropdown': el => LotowheelModule._selectPresetFromDropdown(Number(el.dataset.id)),
    'lotowheel:duplicatePreset': el => LotowheelModule.duplicatePreset(Number(el.dataset.id)),
    'lotowheel:renamePreset': el => LotowheelModule.renamePreset(Number(el.dataset.id)),
    'lotowheel:deletePreset': el => LotowheelModule.deletePreset(Number(el.dataset.id)),
    'lotowheel:recoverVirtualDraws': () => LotowheelModule.recoverVirtualDraws(),
    'lotowheel:createVirtualDrawPrompt': () => LotowheelModule.createVirtualDrawPrompt(),
    'lotowheel:openBloqueoModal': () => LotowheelModule.openBloqueoModal(),
    'lotowheel:saveBloqueo': () => LotowheelModule.saveBloqueo(),
    'lotowheel:cancelConfigModal': () => {
        LotowheelModule._cachedModalSorteoId = null;
        App.closeModal();
    },
    'lotowheel:saveLotowheelConfig': () => LotowheelModule.saveLotowheelConfig(),
    'lotowheel:deleteLotowheelConfig': () => LotowheelModule.deleteLotowheelConfig(),
    'lotowheel:addChip': () => LotowheelModule.addChip(),
    'lotowheel:agregarPartnerOverrideSerie': () => LotowheelModule.agregarPartnerOverrideSerie(),
    'lotowheel:saveCurrentAsPreset': () => LotowheelModule.saveCurrentAsPreset(),
    'lotowheel:simulateProfile': () => LotowheelModule.simulateProfile(),
    'lotowheel:clearPresetSearch': () => LotowheelModule._clearPresetSearch(),
    'lotowheel:clearAccesoNodoSearch': () => LotowheelModule.clearAccesoNodoSearch(),
    'lotowheel:selectAllAccesoNodos': () => LotowheelModule.selectAllAccesoNodos(),
    'lotowheel:clearAllAccesoNodos': () => LotowheelModule.clearAllAccesoNodos(),
    'lotowheel:uploadBrandingAsset': el => LotowheelModule.uploadBrandingAsset(el.dataset.param),
    'lotowheel:resetBrandingAsset': el => LotowheelModule.resetBrandingAsset(el.dataset.param),
    // --- LotoWheel Simulator ---
    'simulator:close': () => LotowheelSimulator.close(),
    'simulator:runSimulation': () => LotowheelSimulator.runSimulation(),
    'simulator:saveAndClose': () => LotowheelSimulator.saveAndClose(),

    // --- Pozos ---
    'digi12:openModal': el => Digi12Module.openModal(el.dataset.id ? Number(el.dataset.id) : null),
    'digi12:save': () => Digi12Module.save(),
    'digi12:financiarColeccion': el => Digi12Module.financiarColeccion(Number(el.dataset.id)),
    'digi12:crearGrupoDePozos': () => Digi12Module.crearGrupoDePozos(),
    'pozos:openCreateModal': () => PozosModule.openCreateModal(),
    'pozos:saveModal': () => PozosModule.saveModal(),

    // --- Platform Nodos ---
    'nodos:toggleExpandAll': () => PlatformNodos.toggleExpandAll(),
    'nodos:toggleInactivos': () => PlatformNodos.toggleInactivos(),
    'nodos:addChildRoot': () => PlatformNodos.addChildWizard(App.currentUser?.nodo_id || 1),
    'nodos:save': () => PlatformNodos.save(),
    'nodos:moverNodo': () => PlatformNodos.moverNodo(),
    'nodos:toggleAccordion': el => PlatformNodos.toggleAccordion(el.dataset.param),
    'nodos:uploadBranding': el => PlatformNodos.uploadBranding(el.dataset.param),
    'nodos:resetBranding': el => PlatformNodos.resetBranding(el.dataset.param),
    'nodos:toggleSecretVisibility': () => PlatformNodos.toggleSecretVisibility(),
    'nodos:onTipoChange': () => PlatformNodos.onTipoChange(),
    'nodos:onPaisChange': () => PlatformNodos.onPaisChange(),
    'nodos:addPasarelaProvider': () => PlatformNodos.addPasarelaProvider(),
    'nodos:agregarPartnerOverrideSerieEnNodo': () => PlatformNodos.agregarPartnerOverrideSerieEnNodo(),
    'nodos:removePasarelaProvider': el => PlatformNodos.removePasarelaProvider(el.dataset.param),

    // --- Platform Dispositivos ---
    // Saldos de la Red
    'saldos-red:abrirModalRecarga': el =>
        abrirModalDeRecargaDeSaldoPrepago(
            parseInt(el.dataset.nodoId),
            el.dataset.nodoNombre,
            el.dataset.nodoMoneda || null
        ),
    'saldos-red:cargarMasNodos': () => cargarMasNodosDeSaldosPrepago(),
    'saldos-red:pagarDelegadoTicket': el => pagarDelegadoTicket(el),
    'saldos-red:filtrarPorChipDeNivel': el => filtrarPorChipDeNivel(el),
    'saldos-red:verBilhetesDelNodo': el => verBilhetesDelNodo(el),
    'tickets:switchBilhetesSubtab': el => TicketsModule.switchBilhetesSubtab(el),
    'finanzas:switchWalletSubtab': el => FinanzasModule.switchWalletSubtab(el),
    'recarga-prepago:revisar': el => revisarRecargaPrepago(el),
    'recarga-prepago:atras': el => atrasRecargaPrepago(el),
    'recarga-prepago:confirmar': el => confirmarRecargaPrepago(el),
    'recarga-prepago:cancelar': () => cancelarRecargaPrepago(),

    'dispositivos:save': () => PlatformDispositivos.save(),
    'dispositivos:reiniciar': () => PlatformDispositivos.reiniciar(),
    'dispositivos:openNodoModalFromDispositivo': () => PlatformDispositivos.openNodoModalFromDispositivo(),
    'dispositivos:onGameTypeChange': () => PlatformDispositivos.onGameTypeChange(),

    // --- Platform Administradores ---
    'administradores:openModal': () => PlatformAdministradores.openModal(),
    'admin:togglePasswordVisibility': () => PlatformAdministradores.togglePasswordVisibility(),
    'admin:generateSecurePassword': () => PlatformAdministradores.generateSecurePassword(),

    // --- Platform Module ---
    'platform:saveUsuario': () => PlatformModule.saveUsuario(),
    'platform:onRolChange': () => PlatformModule.onRolChange(),

    // --- StatsModal ---
    'stats:setPeriod': el => StatsModal.setPeriod(el.dataset.period),
    'stats:toggleRangoInputs': () => StatsModal.toggleRangoInputs(),
    'stats:applyRango': () => StatsModal.applyRango(),

    // --- NodeStatisticsDashboard ---
    'nodeStats:close': () => NodeStatisticsDashboard.close(),
    'nodeStats:setPeriod': el => NodeStatisticsDashboard.setPeriod(el.dataset.period),
    'nodeStats:toggleCustomRange': () => NodeStatisticsDashboard.toggleCustomRangeInputs(),
    'nodeStats:applyCustomRange': () => NodeStatisticsDashboard.applyCustomRange(),
    'nodeStats:copyText': () => NodeStatisticsDashboard.copyStatsAsPlainText(),
    'nodeStats:downloadImage': () => NodeStatisticsDashboard.downloadChartsAsImage(),
    'nodeStats:shareWhatsapp': () => NodeStatisticsDashboard.shareViaWhatsApp(),

    // --- ContratoModal ---
    'contrato:save': () => ContratoModal.save(),
    'contrato:abrirCrearPromoScoped': () => ContratoModal.abrirCrearPromoScoped(),
    'contrato:abrirClonarPreset': () => ContratoModal.abrirClonarPreset(),
    'contrato:toggleObligacionForm': () => ContratoModal.toggleObligacionForm(),
    'contrato:crearObligacion': () => ContratoModal.crearObligacion(),
    'contrato:wizardNext': () => ContratoModal.wizardNext(),
    'contrato:wizardPrev': () => ContratoModal.wizardPrev(),
    'contrato:onTipoCardClick': el => ContratoModal.onTipoCardClick(el),
    'contrato:onCalculoCardClick': el => ContratoModal.onCalculoCardClick(el),
    'contrato:loadIncentivos': () => ContratoModal.loadIncentivos(),
    'contrato:eliminarPromoScoped': el =>
        ContratoModal.eliminarPromoScoped(Number(el.dataset.id), el.dataset.nombre || ''),
    'contrato:editarPromoScoped': el => ContratoModal.editarPromoScoped(Number(el.dataset.id)),
    'contrato:clonarPresetScoped': el =>
        ContratoModal.clonarPresetScoped(Number(el.dataset.id), el.dataset.nombre || ''),
    'contrato:eliminarObligacion': el =>
        ContratoModal.eliminarObligacion(Number(el.dataset.id), el.dataset.nombre || ''),
    // --- NodeStatisticsDashboard (extra) ---
    'nodeStats:toggleCortesSection': () => NodeStatisticsDashboard._toggleCortesSection(),
    'nodeStats:setTab': el => NodeStatisticsDashboard.setTab(el.dataset.tab),
    'nodeStats:verFichaJugador': el => NodeStatisticsDashboard.verFichaJugador(Number(el.dataset.cliente)),
    'nodeStats:cerrarFichaJugador': () => NodeStatisticsDashboard.cerrarFichaJugador(),

    // --- Tickets ---
    'tickets:searchTicket': () => TicketsModule.searchTicket(),
    'tickets:loadTickets': () => TicketsModule.loadTickets(),

    // --- Operación (ex-Actividad) ---
    'actividad:loadIntegradores': () => ActividadModule.loadIntegradores(),

    // --- Finanzas ---
    'finanzas:loadMovimientos': () => FinanzasModule.loadMovimientos(),
    'b2b:recargar': () => FinanzasB2BModule.load(),
    // Incidencias VLT: decisiones supervisadas sobre entregas de efectivo.
    'b2b:abrirNuevaContraparte': () => FinanzasB2BModule.abrirNuevaContraparte(),
    'b2b:guardarContraparte': () => FinanzasB2BModule.guardarContraparte(),
    'b2b:wizardAbrir': () => FinanzasB2BModule.wizardAbrir(),
    'b2b:wizardSiguiente': () => FinanzasB2BModule.wizardSiguiente(),
    'b2b:wizardAtras': () => FinanzasB2BModule.wizardAtras(),
    'b2b:wizardProbar': () => FinanzasB2BModule.wizardProbarConexion(),
    'b2b:wizardCrear': () => FinanzasB2BModule.wizardCrear(),
    'b2b:wizardActivar': () => FinanzasB2BModule.wizardActivar(),
    'b2b:abrirDominios': el => FinanzasB2BModule.abrirDominios(Number(el.dataset.id)),
    'b2b:copiarDominios': () => FinanzasB2BModule.copiarDominios(),
    'b2b:guardarDominios': el => FinanzasB2BModule.guardarDominios(Number(el.dataset.id)),
    'b2b:asociarNodo': el => FinanzasB2BModule.asociarNodo(Number(el.dataset.id)),
    'b2b:cambiarEstado': el => FinanzasB2BModule.cambiarEstado(Number(el.dataset.id), el.dataset.estado),
    'b2b:confirmarAsociarNodo': el => FinanzasB2BModule.confirmarAsociarNodo(Number(el.dataset.id)),
    'b2b:confirmarActivar': el => FinanzasB2BModule.confirmarActivar(Number(el.dataset.id)),
    // Pasarelas (movido desde Operación): quick-filter + detalle de transacción.
    'finanzas:loadPasarelas': () => FinanzasModule.loadPasarelas(),
    'finanzas:quickFilterPasarela': el => FinanzasModule.quickFilterPasarela(el.dataset.quickfilter),
    'finanzas:verDetallePasarela': el => FinanzasModule.verDetallePasarela(el.dataset.id),
    'finanzas:crearProveedorPasarela': () => FinanzasModule.crearProveedorPasarela(),
    'finanzas:editarProveedorPasarela': el => FinanzasModule.editarProveedorPasarela(el.dataset.id),
    'finanzas:guardarProveedorPasarela': el => FinanzasModule.guardarProveedorPasarela(el.dataset.id),
    'finanzas:probarProveedorPasarela': el => FinanzasModule.probarProveedorPasarela(el.dataset.id),
    'finanzas:eliminarProveedorPasarela': el =>
        FinanzasModule.eliminarProveedorPasarela(el.dataset.id, el.dataset.nombre),
    'finanzas:cambiarNodoAsignacionPasarela': el => FinanzasModule.loadAsignacionPasarelaPorNodo(Number(el.value)),
    'finanzas:openCorteModal': () => FinanzasModule.openCorteModal(),
    'finanzas:openCorteCascadaModal': () => FinanzasModule.openCorteCascadaModal(),
    'finanzas:openLiquidacionModal': () => FinanzasModule.openLiquidacionModal(),
    'finanzas:loadCuentasCorrientes': () => FinanzasModule.loadCuentasCorrientes(),
    'finanzas:loadContratos': () => FinanzasModule.loadContratos(),
    'finanzas:loadAdelantos': () => FinanzasModule.loadAdelantos(),
    'finanzas:openAdelantoModal': () => FinanzasModule.openAdelantoModal(),
    'finanzas:loadAlertas': () => FinanzasModule.loadAlertas(),
    'finanzas:marcarTodasAlertasLeidas': () => FinanzasModule.marcarTodasAlertasLeidas(),
    'finanzas:openAjusteCuentaModal': () => FinanzasModule.openAjusteCuentaModal(),
    'finanzas:retryAllPendingSettlements': () => FinanzasModule.retryAllPendingSettlements(),
    'finanzas:saveCorte': () => FinanzasModule.saveCorte(),
    'finanzas:saveCorteCascada': () => FinanzasModule.saveCorteCascada(),
    'finanzas:abrirResolverDisputa': el => FinanzasModule.abrirResolverDisputa(Number(el.dataset.id)),
    'finanzas:continuarResolverDisputa': () => FinanzasModule.continuarResolverDisputa(),
    'finanzas:volverResolverDisputa': () => FinanzasModule.volverResolverDisputa(),
    'finanzas:confirmarResolverDisputa': () => FinanzasModule.confirmarResolverDisputa(),
    'finanzas:calcularPreviaCascada': () => FinanzasModule.calcularPreviaCascada(),
    'finanzas:exportarPreviaCascada': () => FinanzasModule.exportarPreviaCascada(),
    'finanzas:saveAdelanto': () => FinanzasModule.saveAdelanto(),
    'finanzas:saveLiquidacion': () => FinanzasModule.saveLiquidacion(),
    'finanzas:saveAjusteCuenta': () => FinanzasModule.saveAjusteCuenta(),
    'finanzas:savePagoLiquidacion': () => FinanzasModule.savePagoLiquidacion(),
    // Acciones de fila (antes onclick inline en templates)
    'finanzas:editComision': el =>
        FinanzasModule.editComision(Number(el.dataset.id), Number(el.dataset.nodoId), el.dataset.nodoNombre || ''),
    'finanzas:viewLiquidacion': el => FinanzasModule.viewLiquidacion(Number(el.dataset.id)),
    'finanzas:openPagarLiquidacionModal': el =>
        FinanzasModule.openPagarLiquidacionModal(Number(el.dataset.id), Number(el.dataset.nodoId)),
    'finanzas:viewHistorialCuenta': el => FinanzasModule.viewHistorialCuenta(Number(el.dataset.id)),
    'finanzas:editContrato': el =>
        FinanzasModule.editContrato(Number(el.dataset.id), Number(el.dataset.nodoId), el.dataset.nodoNombre || ''),
    'finanzas:confirmarAdelanto': el => FinanzasModule.confirmarAdelanto(Number(el.dataset.id)),
    'finanzas:rechazarAdelanto': el => FinanzasModule.rechazarAdelanto(Number(el.dataset.id)),
    'finanzas:devolverAdelanto': el =>
        FinanzasModule.devolverAdelanto(Number(el.dataset.id), Number(el.dataset.nodoId)),
    'finanzas:devolverRetenida': el => FinanzasModule.devolverRetenida(el.dataset.id),
    'finanzas:devolverPorPix': el => FinanzasModule.devolverPorPix(el.dataset.id),
    'finanzas:cargarMasPasarelas': () => FinanzasModule.cargarMasPasarelas(),
    'finanzas:retrySettlement': el => FinanzasModule.retrySettlement(Number(el.dataset.id)),
    'finanzas:resolveSettlement': el => FinanzasModule.resolveSettlement(Number(el.dataset.id)),
    'finanzas:abrirReintentoMovimientoRound': el =>
        FinanzasModule.abrirReintentoMovimientoRound(el.dataset.ref, el.dataset.fase),
    'finanzas:confirmarReintentoMovimientoRound': () => FinanzasModule.confirmarReintentoMovimientoRound(),
    'finanzas:irACorte': el => App.switchTab('finanzas', 'cortes'),
    'finanzas:confirmarCorte': el => FinanzasModule.confirmarCorte(Number(el.dataset.id)),
    'finanzas:disputarCorte': el => FinanzasModule.disputarCorte(Number(el.dataset.id)),
    'finanzas:generarLinkConformidadCorte': el => FinanzasModule.generarLinkConformidadCorte(Number(el.dataset.id)),
    'finanzas:confirmarLiquidacion': el => FinanzasModule.confirmarLiquidacion(Number(el.dataset.id)),
    'finanzas:disputarLiquidacion': el => FinanzasModule.disputarLiquidacion(Number(el.dataset.id)),
    'finanzas:loadConformidadPendientes': () => FinanzasModule.loadConformidadPendientes(),
    'finanzas:abrirModalVoucherCorte': el => FinanzasModule.abrirModalVoucherCorte(el.dataset.token, el.dataset.id),
    'finanzas:abrirModalVoucherLiquidacion': el =>
        FinanzasModule.abrirModalVoucherLiquidacion(el.dataset.token, el.dataset.id),
    'finanzas:copiarLink': el => {
        const url = el.dataset.url || '';
        if (!url) {
            App.toast('Sin link disponible', 'warning');
            return;
        }
        navigator.clipboard.writeText(url).then(() => App.toast('Link copiado', 'success'));
    },
    'finanzas:subirVoucherDesdeModal': el =>
        FinanzasModule._subirVoucherDesdeModal(el.dataset.token, el.dataset.corteId),
    'finanzas:subirVoucherDesdeModalGenerico': el =>
        FinanzasModule._subirVoucherDesdeModalGenerico(el.dataset.token, el.dataset.itemId, el.dataset.sufijo),

    // --- Finanzas: Exportacion de reportes ---
    'finanzas:exportCortesExcel': () => FinanzasModule.exportCortes('xlsx'),
    'finanzas:exportLiquidacionesExcel': () => FinanzasModule.exportLiquidaciones('xlsx'),
    'finanzas:exportMovimientosExcel': () => FinanzasModule.exportMovimientos('xlsx'),
    'finanzas:exportMovimientosPdf': () => FinanzasModule.exportMovimientos('pdf'),
    'finanzas:relatorioSetModo': el => FinanzasModule.relatorioSetModo(el.dataset.modo),
    'finanzas:switchGatewaySubtab': el => FinanzasModule.switchGatewaySubtab(el),
    'finanzas:switchCierresSubtab': el => FinanzasModule.switchCierresSubtab(el),
    'finanzas:verRelatorioEnPantalla': () => FinanzasModule.verRelatorioEnPantalla(),
    'finanzas:exportFechamentoExcel': () => FinanzasModule.exportFechamento('xlsx'),
    'finanzas:exportFechamentoEntregaveisExcel': () => FinanzasModule.exportFechamento('xlsx', true),

    // --- Marketing (dinámicos) ---
    'marketing:togglePromocion': el =>
        MarketingModule.togglePromocion(Number(el.dataset.id), Number(el.dataset.estado)),
    'marketing:abrirModalEditar': el => MarketingModule.abrirModalEditar(Number(el.dataset.id)),
    'marketing:eliminarPromocion': el => MarketingModule.eliminarPromocion(Number(el.dataset.id), el.dataset.nombre),
    'marketing:clonarComoPlantilla': el => MarketingModule.clonarComoPlantilla(Number(el.dataset.id)),
    'marketing:eliminarPlantilla': el => MarketingModule.eliminarPlantilla(Number(el.dataset.id), el.dataset.nombre),
    'marketing:clonarPreset': el => MarketingModule.clonarPreset(Number(el.dataset.id)),
    'marketing:clonarPresetFromSelector': el => {
        App.closeModal();
        MarketingModule.clonarPreset(Number(el.dataset.id));
    },
    'marketing:marcarInfluencer': el => MarketingModule.marcarInfluencer(Number(el.dataset.id)),
    'marketing:selectTrigger': el => MarketingModule._selectTrigger(el.dataset.trigger),
    // --- Marketing (estáticos) ---
    'marketing:mostrarSelectorPlantillas': () => MarketingModule.mostrarSelectorPlantillas(),
    'marketing:abrirModalCrearPlantilla': () => MarketingModule.abrirModalCrearPlantilla(),
    'marketing:toggleExpandAllPresets': () => MarketingModule.toggleExpandAllPresets(),
    'marketing:guardarPromocion': () => MarketingModule.guardarPromocion(),
    'marketing:addConditionRow': () => MarketingModule._addConditionRow(),
    'marketing:addHitoRow': () => MarketingModule._addHitoRow(),
    'marketing:addNivelDePerdidaRow': () => MarketingModule._addNivelDePerdidaRow(),
    'marketing:toggleAdvanced': () => MarketingModule._toggleAdvanced(),

    // --- Marketing Cupones (§5) ---
    'marketing-cupones:abrirWizardNueva': () => MarketingCuponesModule.abrirWizardNueva(),
    'marketing-cupones:toggleActivo': el =>
        MarketingCuponesModule.toggleActivo(Number(el.dataset.id), Number(el.dataset.estado)),

    // --- Marketing Tickets al portador ---
    'marketing-tickets:abrirModalCrear': () => MarketingTicketsModule.abrirModalCrear(),
    'marketing-tickets:abrirModalEditar': el => MarketingTicketsModule.abrirModalEditar(Number(el.dataset.id)),
    'marketing-tickets:toggleActivo': el =>
        MarketingTicketsModule.toggleActivo(Number(el.dataset.id), Number(el.dataset.estado)),
    'marketing-tickets:eliminarTicket': el =>
        MarketingTicketsModule.eliminarTicket(Number(el.dataset.id), el.dataset.nombre || ''),
    'marketing-tickets:abrirSelectorPresets': () => MarketingTicketsModule.abrirSelectorPresets(),
    'marketing-tickets:activarPreset': el => MarketingTicketsModule.activarPreset(Number(el.dataset.id)),
    'marketing-tickets:guardar': () => MarketingTicketsModule.guardar(),

    // --- Sistema ---
    'sistema:loadAuditoria': () => SistemaModule.loadAuditoria(),
    'sistema:showLogDetail': el => SistemaModule.showLogDetail(el.dataset.id),
    'sistema:loadSesiones': () => SistemaModule.loadSesiones(),
    'sistema:refreshSeriesTickets': () => SistemaModule.loadSeriesTickets(),
    'sistema:crearSerieTickets': () => SistemaModule.abrirModalCrearSerie(),
    'sistema:editarSerieTickets': el => SistemaModule.abrirModalEditarSerie(Number(el.dataset.id)),
    'sistema:borrarSerieTickets': el => SistemaModule.borrarSerie(Number(el.dataset.id)),
    'sistema:setDefaultGlobalSerie': el => SistemaModule.setDefaultGlobal(Number(el.dataset.id)),
    'sistema:rotarKeySerie': el => SistemaModule.rotarKey(Number(el.dataset.id)),
    'sistema:generarKeyRandom': () => SistemaModule.generarKeyRandom(),
    'sistema:guardarSerieTickets': () => SistemaModule.guardarSerie(),
    'sistema:loadFleetCrashes': () => SistemaModule.loadFleetCrashes(),
    'sistema:loadFleetWhatsapp': () => SistemaModule.loadFleetWhatsapp(),
    'sistema:nuevaWhatsappConfig': () => SistemaModule.nuevaWhatsappConfig(),
    'sistema:guardarWhatsappConfig': () => SistemaModule.guardarWhatsappConfig(),

    // --- Administradores (dinámicos) ---
    'admin:openModal': el => PlatformAdministradores.openModal(Number(el.dataset.id)),
    'entidades:verFicha': el => PlatformAdministradores.verFicha(Number(el.dataset.id)),
    // Desde la ficha de entidad, saltar al nodo dueño: cambia al tab Red Operativa
    // (el árbol) Y selecciona el nodo. Sin el switchTab, selectNode operaría sobre
    // un árbol no visible (bug detectado en revisión adversarial).
    'entidades:irAlNodo': el => {
        App.switchTab('plataforma', 'red');
        PlatformNodos.selectNode(Number(el.dataset.id));
    },
    'admin:delete': el => PlatformAdministradores.delete(Number(el.dataset.id)),
    // Resetear acceso y Mover desde la ficha de entidad (centro de operaciones).
    'entidades:resetPassword': el =>
        PlatformAdministradores.resetPassword(Number(el.dataset.id), el.dataset.nombre, el.dataset.rol),
    'entidades:mover': el => PlatformAdministradores.mover(Number(el.dataset.id)),
    'entidades:guardarMover': () => PlatformAdministradores.guardarMover(),
    'admin:selectCrearNuevo': () => PlatformAdministradores._selectCrearNuevo(),

    // --- Dispositivos (dinámicos) ---
    'dispositivos:verFicha': el => PlatformDispositivos.verFicha(Number(el.dataset.id)),
    'dispositivos:cerrarFicha': () => PlatformFichaDispositivos.cerrar(),
    'dispositivos:guardarNotaUbicacion': el => PlatformFichaDispositivos.guardarNotaUbicacion(Number(el.dataset.id)),
    // Panel de acceso INLINE de la ficha: ver QR / copiar enlace / bajar APK.
    'dispositivos:verQrAcceso': el => PlatformNodos.verQrAccesoDispositivo(Number(el.dataset.id)),
    'dispositivos:copiarEnlaceAcceso': el => PlatformNodos.copiarEnlaceAccesoDispositivo(Number(el.dataset.id)),
    'dispositivos:bajarApk': el => PlatformNodos.bajarApkDispositivo(Number(el.dataset.id)),
    // Artefactos del aparato Android: hornear+QR / reasignar (identidad nueva).
    'dispositivos:hornearApk': el => PlatformNodos.hornearApkDesdeFicha(Number(el.dataset.id)),
    'dispositivos:reasignarAparato': el =>
        PlatformNodos.reasignarAparato(Number(el.dataset.id), el.dataset.nombre || '', el.dataset.pv || ''),
    // Artefacto web: obtener link de acceso (idempotente — devuelve el vigente o
    // crea el primero si no hay). Lo usan "Ver QR"/"Copiar enlace"/"Generar link".
    // data-tiene-token='1' → el dispositivo está operativo (sesión activa).
    'dispositivos:obtenerAcceso': el =>
        PlatformNodos.obtenerAccesoDispositivoWeb(
            Number(el.dataset.id),
            el.dataset.tieneToken === '1',
            el.dataset.nombre || '',
            el.dataset.pv || '',
            false,
            el.dataset.tipo || ''
        ),
    // "Regenerar" link web: FUERZA uno nuevo (invalida el anterior, con confirmación).
    'dispositivos:regenerarLinkWeb': el =>
        PlatformNodos.obtenerAccesoDispositivoWeb(
            Number(el.dataset.id),
            el.dataset.tieneToken === '1',
            el.dataset.nombre || '',
            el.dataset.pv || '',
            true,
            el.dataset.tipo || ''
        ),
    // Desde la ficha del dispositivo, saltar al nodo dueño (mismo patrón que
    // entidades:irAlNodo: switchTab al árbol Y seleccionar, para no operar sobre
    // un árbol no visible).
    'dispositivos:irAlNodo': el => {
        PlatformFichaDispositivos.cerrar();
        App.switchTab('plataforma', 'red');
        PlatformNodos.selectNode(Number(el.dataset.id));
    },
    'dispositivos:authorize': el => PlatformDispositivos.authorize(el.dataset.codigo),
    'dispositivos:authorizeWizard': el => PlatformDispositivos.authorizeWizard(el.dataset.codigo, el.dataset.serial),
    'dispositivos:deletePending': el => PlatformDispositivos.delete(Number(el.dataset.id), true),
    'dispositivos:openModal': el => PlatformDispositivos.openModal(Number(el.dataset.id)),
    'dispositivos:reset': el => PlatformDispositivos.reset(Number(el.dataset.id)),
    'dispositivos:toggleActivo': el =>
        PlatformDispositivos.toggleActivo(Number(el.dataset.id), el.dataset.activo === '1'),
    'dispositivos:openFleetInfo': el => PlatformDispositivos.openFleetInfo(Number(el.dataset.id), el.dataset.tipo),
    'dispositivos:fleetRefreshNow': el => PlatformDispositivos.fleetRefreshNow(Number(el.dataset.id)),
    'dispositivos:tvComando': el => PlatformDispositivos.tvComando(Number(el.dataset.id)),
    'dispositivos:tvComandoEnviar': el => PlatformDispositivos.tvComandoEnviar(Number(el.dataset.id)),
    'dispositivos:delete': el => PlatformDispositivos.delete(Number(el.dataset.id)),

    // --- Nodos (dinámicos) ---
    'nodos:addChildWizard': el => PlatformNodos.addChildWizard(Number(el.dataset.id)),
    'nodos:crearUsuarioParaNodo': el => PlatformNodos.crearUsuarioParaNodo(Number(el.dataset.id)),
    'nodos:generarLinkAccesoPromotor': el => PlatformNodos.generarLinkAccesoPromotor(Number(el.dataset.id)),
    'nodos:copiarAccesoPromotor': el => PlatformNodos.copiarAccesoPromotor(Number(el.dataset.id)),
    'nodos:registrarPagoPromotor': el => PlatformNodos.registrarPagoPromotor(Number(el.dataset.id)),
    'nodos:toggleExpand': el => PlatformNodos.toggleExpand(Number(el.dataset.id)),
    'nodos:selectNode': el => {
        PlatformNodos.selectNode(Number(el.dataset.id));
        if (el.dataset.expandable === '1') PlatformNodos.toggleExpand(Number(el.dataset.id));
    },
    'nodos:showAlertasNodo': el => PlatformNodos.showAlertasNodo(Number(el.dataset.id)),
    'nodos:viewStats': el => PlatformNodos.viewStats(Number(el.dataset.id)),
    'nodos:openModal': el => PlatformNodos.openModal(Number(el.dataset.id)),
    'nodos:addChild': el => PlatformNodos.addChild(Number(el.dataset.id)),
    'nodos:delete': el => PlatformNodos.delete(Number(el.dataset.id), el.dataset.nombre),
    'nodos:reactivar': el => PlatformNodos.reactivar(Number(el.dataset.id)),

    // --- Tickets (dinámicos) ---
    'tickets:cobrar': el => TicketsModule.cobrarTicket(el.dataset.codigo),
    'tickets:anular': el => TicketsModule.anularTicket(el.dataset.codigo),

    // --- Clientes (dinámicos) ---
    'clientes:viewJugadorDetail': el => ClientesModule.viewJugadorDetail(Number(el.dataset.id)),
    'clientes:viewMovimientos': el => ClientesModule.viewMovimientos(Number(el.dataset.id)),
    'clientes:exportAudit': el => ClientesModule.exportAudit(el.dataset.formato),
    'clientes:inhabilitarJugador': el => ClientesModule.inhabilitarJugador(el.dataset.telefono),
    'clientes:filtrarChip': el => ClientesModule.filtrarChip(el.dataset.chip),
    'clientes:cambiarPeriodoStats': el => ClientesModule.cambiarPeriodoStats(el.dataset.periodo),
    'clientes:cambiarMetricaBarra': el => ClientesModule.cambiarMetricaBarra(el.dataset.metrica),
    'clientes:limpiarFiltros': () => ClientesModule.limpiarFiltros(),
    'clientes:enviarPushJugador': el => ClientesModule.enviarPushJugador(Number(el.dataset.id)),
    'clientes:enviarPushSegmento': el => ClientesModule.enviarPushSegmento(el.dataset.segmento, el.dataset.label),
    'clientes:enviarPushFiltroActual': () => ClientesModule.enviarPushFiltroActual(),
    'clientes:cambiarPeriodoGrafico': el => ClientesModule.cambiarPeriodoGrafico(el.dataset.periodo),
    'clientes:toggleOndeMelhorar': () => ClientesModule.toggleOndeMelhorar(),
    'clientes:toggleMostrarExcluidos': () => ClientesModule.toggleMostrarExcluidos(),
    'clientes:toggleBloqueo': el => ClientesModule.toggleBloqueo(el.dataset.telefono, Number(el.dataset.bloqueado)),
    'clientes:resetPinLink': el => ClientesModule.resetPinLink(el.dataset.telefono),

    // --- Pozos (dinámicos) ---
    'pozos:openEditModal': el => PozosModule.openEditModal(Number(el.dataset.id)),
    'pozos:saveAndResetPozo': () => PozosModule.saveAndResetPozo(),
    'pozos:deletePozo': () => PozosModule.deletePozo(),
    'pozos:aplicarPresetDeReferencia': () => PozosModule.aplicarPresetDeReferencia(),
    'pozos:iniciarConversionAlModeloDeNiveles': () => PozosModule.iniciarConversionAlModeloDeNiveles(),

    // --- Nodos adicionales ---
    'nodos:openContratoModal': el => PlatformNodos.openContratoModal(Number(el.dataset.id)),
    'nodos:editarComisionDesdeEdicion': el => PlatformNodos.editarComisionDesdeEdicion(Number(el.dataset.id)),
    'nodos:moverDesdeEdicion': el => PlatformNodos.moverDesdeEdicion(Number(el.dataset.id)),
    'nodos:toggleActivoDesdeEdicion': el => PlatformNodos.toggleActivoDesdeEdicion(Number(el.dataset.id)),
    'nodos:toggleExclusion': el => PlatformNodos.toggleExclusion(Number(el.dataset.id), el.dataset.tipo),
    'nodos:eliminarDesdeEdicion': el => PlatformNodos.eliminarDesdeEdicion(Number(el.dataset.id)),
    'nodos:openJuegosModal': el => PlatformNodos.openJuegosModal(Number(el.dataset.id)),
    'nodos:heredarJuegos': el => PlatformNodos.heredarJuegos(Number(el.dataset.id)),
    'nodos:toggleBonusPropio': el =>
        PlatformNodos.toggleBonusPropio(Number(el.dataset.nodo), Number(el.dataset.promo), el.dataset.activar === '1'),
    'nodos:toggleBonusExclusion': el =>
        PlatformNodos.toggleBonusExclusion(
            Number(el.dataset.nodo),
            Number(el.dataset.promo),
            el.dataset.excluir === '1'
        ),
    'nodos:toggleBonusTicketPropio': el =>
        PlatformNodos.toggleBonusTicketPropio(
            Number(el.dataset.nodo),
            Number(el.dataset.promo),
            el.dataset.activar === '1'
        ),
    'nodos:toggleBonusTicketExclusion': el =>
        PlatformNodos.toggleBonusTicketExclusion(
            Number(el.dataset.nodo),
            Number(el.dataset.promo),
            el.dataset.excluir === '1'
        ),
    'nodos:openMoverModal': el => PlatformNodos.openMoverModal(Number(el.dataset.id)),
    'nodos:toggleActivo': el =>
        PlatformNodos.toggleActivo(Number(el.dataset.id), el.dataset.activo === '1', el.dataset.nombre),
    'nodos:verFichaUsuario': el => PlatformNodos.verFichaUsuario(el),
    'nodos:verFichaDispositivo': el => PlatformNodos.verFichaDispositivo(el),
    // Bloques financieros del inspector de nodo (lazy load al expandir).
    'finanzas-nodo:toggle': el => PlatformFichaFinanzasNodo.toggle(el),
    'finanzas-nodo:historialCuenta': el => FinanzasModule.viewHistorialCuenta(Number(el.dataset.id)),
    // Cross-view: del inspector (vista plataforma) a la vista Finanzas. navigate
    // cambia la VISTA visible; switchTab solo cambia tabs dentro de una vista ya
    // visible — por eso hace falta navigate primero (patrón de admin.js:374-377).
    'finanzas-nodo:irACortes': () => {
        App.navigate('finanzas');
        App.switchTab('finanzas', 'cortes');
    },
    'nodos:saveJuegosConfig': el => PlatformNodos._saveJuegosConfig(Number(el.dataset.id)),
    'nodos:toggleDetailAccordion': el => el.classList.toggle('open'),
    'nodos:toggleGrupoHojas': el => PlatformNodos.toggleGrupoHojas(el.dataset.grupoId),
    'nodos:generarApkDispositivo': el =>
        PlatformNodos.generarApkDispositivo(
            Number(el.dataset.id),
            el.dataset.producto,
            el.dataset.subtipo,
            el.dataset.nodoNombre || ''
        ),
    // el.dataset.accion es el nombre del método a llamar (validado en la lista de acciones seguras)
    'nodos:alertaAccion': el => {
        const ACCIONES_SEGURAS = ['openModal', 'openContratoModal', 'openJuegosModal', 'toggleActivo', 'reactivar'];
        if (ACCIONES_SEGURAS.includes(el.dataset.accion)) PlatformNodos[el.dataset.accion](Number(el.dataset.id));
    },

    // --- Setup Wizard ---
    'wizard:next': () => SetupWizard.next(),
    'wizard:prev': () => SetupWizard.prev(),
    'wizard:confirm': () => SetupWizard.confirm(),
    'wizard:goToStep': el => SetupWizard.goToStep(el.dataset.param),

    // --- UI genérico ---
    'ui:removeById': el => {
        const t = document.getElementById(el.dataset.target);
        if (t) t.remove();
    },
    'ui:removeRow': el => {
        const t = document.getElementById(el.dataset.target);
        if (t) t.remove();
    },
    'ui:toggleNextSiblingGrid': el => {
        const next = el.nextElementSibling;
        if (!next) return;
        const isHidden = next.style.display === 'none';
        next.style.display = isHidden ? 'grid' : 'none';
        const span = el.querySelector('span');
        if (span) span.textContent = isHidden ? '▼' : '▶';
    },
};

// ---------------------------------------------------------------------------
// CHANGE actions map
// ---------------------------------------------------------------------------
const CHANGE_HANDLERS = {
    'app:changeSorteoConfig': () => App.changeSorteoConfig(),
    // Los dos selects del grupo de pozos son de CHANGE, no de click.
    'digi12:elegirGrupoDePozos': () => Digi12Module.elegirGrupoDePozos(),
    'digi12:alCambiarPozoDeNivel': () => Digi12Module.alCambiarPozoDeNivel(),
    'b2b:avisarPartnerCode': el => FinanzasB2BModule.avisarPartnerCode(Number(el.dataset.id)),
    'app:changeSorteoRange': () => App.changeSorteoRange(),
    'dispositivos:filter': () => PlatformDispositivos.filter(),
    'dispositivos:onOperacionesChange': el => PlatformDispositivos.onOperacionesChange(el.dataset.param),
    'dispositivos:onGameTypeChange': () => PlatformDispositivos.onGameTypeChange(),
    'dispositivos:onPanelModeChange': el => PlatformDispositivos.onPanelModeChange(el.dataset.prefix, el.dataset.panel),
    'dispositivos:moverSorteo': (el, e) => PlatformDispositivos.onMoverSorteo(el, e),
    'dispositivos:tvComandoAccionChange': () => PlatformDispositivos.tvComandoAccionChange(),
    'platform:onRolChange': () => PlatformModule.onRolChange(),
    'nodos:onTipoChange': () => PlatformNodos.onTipoChange(),
    'nodos:onPaisChange': () => PlatformNodos.onPaisChange(),
    'nodos:onTipoIdentificadorChange': () => PlatformNodos.onTipoIdentificadorChange(),
    'nodos:onShineColorChange': () => PlatformNodos.onShineColorChange(),
    'nodos:onBrandingFileSelected': el => PlatformNodos.onBrandingFileSelected(el.dataset.param),
    'nodos:onGameTypeToggle': (el, e) => PlatformNodos._onGameTypeToggle(e.target),
    'marketing:onRewardFieldChange': el => MarketingModule._onRewardFieldChange(el.dataset.field, el.dataset.value),
    'marketing:onVariableActivoChange': () => MarketingModule._onVariableActivoChange(),
    'marketing:desactivarPremioVariable': () => MarketingModule._desactivarPremioVariable(),
    'marketing:corregirRangoVariable': () => MarketingModule._corregirRangoVariable(),
    'lotowheel:onPozoComunToggle': (el, e) =>
        LotowheelModule._onPozoComunToggle(Number(el.dataset.id), e.target.checked),
    'lotowheel:alternarEditorMultinivel': () => LotowheelModule.alternarEditorMultinivel(),
    'lotowheel:financiarYActivarColeccion': () => LotowheelModule.financiarYActivarColeccion(),
    'lotowheel:onAccesoNodoToggle': (el, e) =>
        LotowheelModule.onAccesoNodoToggle(Number(el.dataset.id), e.target.checked),
    'tickets:applyFilters': () => TicketsModule.applyFilters(),
    'finanzas:loadMovimientos': () => FinanzasModule.loadMovimientos(),
    'finanzas:onTipoNodoChange': () => FinanzasModule.onTipoNodoChange(),
    'contrato:toggleAmortizacionCuotas': () => ContratoModal._toggleAmortizacionCuotasVisibility(),
    'contrato:togglePeriodicidad': () => ContratoModal.togglePeriodicidad(),
    'contrato:toggleConformidadPlazo': () => ContratoModal._toggleConformidadPlazoVisibility(),
    'contrato:onAbsorbeChange': () => ContratoModal._onObligacionAbsorbeChange(),
    'marketing:onBuscarTipoChange': () => MarketingModule.onBuscarTipoChange(),
    'marketing:onCondFactChange': el => MarketingModule._onCondFactChange(el.dataset.row),
    'marketing:onRewardTypeChange': () => MarketingModule._onRewardTypeChange(),
    'marketing:onCondModeChange': () => MarketingModule._onCondModeChange(),
    'marketing:toggleHitos': () => MarketingModule._toggleHitos(),
    'marketing:seleccionarEscenarioPreview': el => MarketingModule._seleccionarEscenarioPreview(el.dataset.escenario),
    'marketing:eliminarHito': el => MarketingModule._eliminarHito(el.dataset.target),
    // Checkboxes de límites en modal de promoción (muestran/ocultan campos)
    'marketing:toggleLimitCliente': el => {
        document.getElementById('promoMaxCliente').style.display = el.checked ? 'block' : 'none';
    },
    'marketing:toggleLimitGlobal': el => {
        document.getElementById('promoMaxGlobal').style.display = el.checked ? 'block' : 'none';
    },
    'marketing:toggleLimitPresupuesto': el => {
        document.getElementById('promoPresupuesto').style.display = el.checked ? 'block' : 'none';
    },
    'marketing:toggleEspera': el => {
        document.getElementById('promoEsperaHoras').style.display = el.checked ? 'block' : 'none';
    },
    'marketing:toggleHorario': el => {
        document.getElementById('promoHorarioPanel').style.display = el.checked ? 'block' : 'none';
    },
    'marketing:toggleWagering': el => {
        document.getElementById('promoWageringPanel').style.display = el.checked ? 'block' : 'none';
        if (el.checked) App.Marketing.cargarPresetsDePasarelaEnSelector();
    },
    'marketing:cambiarProcedenciaWagering': () => App.Marketing.sincronizarVisibilidadDeFronteraWagering(),
    'marketing:toggleRequisitos': el => {
        document.getElementById('promoRequisitosPanel').style.display = el.checked ? 'block' : 'none';
    },
    // --- Marketing Tickets al portador ---
    'marketing-tickets:onEfectoTipoChange': () => MarketingTicketsModule.onEfectoTipoChange(),
    'marketing-tickets:onScopeChange': () => MarketingTicketsModule.onScopeChange(),
    // --- LotoWheel ---
    'lotowheel:updateChipValue': (el, e) => LotowheelModule.updateChipValue(Number(el.dataset.index), e.target.value),
    'lotowheel:updateChipColor': (el, e) =>
        LotowheelModule.updateChip(Number(el.dataset.index), 'color', e.target.value),
    'lotowheel:onTipoRuletaChange': () => LotowheelModule.onTipoRuletaChange(),
    'lotowheel:validatePremioMaximo': () => LotowheelModule.validatePremioMaximo(),
    'lotowheel:onMonedaChange': () => LotowheelModule.onMonedaChange(),
    'lotowheel:onBrandingFileSelected': el => LotowheelModule.onBrandingFileSelected(el.dataset.param),
    'lotowheel:onZeroDisplayModeChange': () => LotowheelModule.onZeroDisplayModeChange(),
    // --- LotoWheel Simulator ---
    'simulator:onResolverChange': () => LotowheelSimulator.onResolverChange(),
    'simulator:onEscenarioChange': () => LotowheelSimulator.onEscenarioChange(),
};

// ---------------------------------------------------------------------------
// INPUT actions map
// ---------------------------------------------------------------------------
const INPUT_HANDLERS = {
    'app:filterSorteos': () => App.filterSorteos(),
    'nodos:filter': el => PlatformNodos.filter(el.value),
    'dispositivos:filter': () => PlatformDispositivos.filter(),
    'marketing:debounceBuscar': () => MarketingModule.debounceBuscar(),
    // Premio variable: revalida MIENTRAS se escribe, no al guardar.
    'marketing:onVariableCampoChange': () => MarketingModule._onVariableCampoChange(),
    'marketing-tickets:onDescripcionTicketInput': () => MarketingTicketsModule.onDescripcionTicketInput(),
    // Sliders del simulador — el.id contiene el param (ej: 'simParamLocales')
    'simulator:onSimParamChange': el => LotowheelSimulator.onSimParamChange(el.id),
    // Preset search y acceso search de lotowheel
    'lotowheel:onPresetSearch': el => {
        LotowheelModule._onPresetSearch(el.value);
        LotowheelModule._onPresetSearchFocus();
    },
    'lotowheel:onAccesoSearch': el => LotowheelModule.onAccesoSearch(el.value),
    // Slider de pesos del preset (lotowheel-modal-algoritmo.js) — lee data-key
    'lotowheel:onSliderInput': (el, e) => LotowheelModule._onSliderInput(el.dataset.key, e.target.value),
    // Inputs de etiqueta personalizada del cero (modo POR_TEXTO)
    'lotowheel:onZeroDisplayLabelInput': () => LotowheelModule.onZeroDisplayLabelInput(),
};

// ---------------------------------------------------------------------------
// KEYUP actions map
// ---------------------------------------------------------------------------
const KEYUP_HANDLERS = {
    'tickets:searchOnEnter': (_el, e) => {
        if (e.key === 'Enter') TicketsModule.searchTicket();
    },
};

// ---------------------------------------------------------------------------
// Delegation setup — se ejecuta cuando el DOM esta listo
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
    // CLICK delegation
    // Ignorar <form> — su data-action se maneja en el submit handler, no en click
    document.addEventListener('click', e => {
        // TabbedModal tabs — genérico para cualquier modal que use el componente.
        const tab = e.target.closest('.modal-tab[data-modal]');
        if (tab) {
            TabbedModal.setTab(tab.dataset.modal, tab.dataset.tab);
            return;
        }

        const el = e.target.closest('[data-action]');
        if (!el || el.tagName === 'FORM') return;
        const handler = CLICK_HANDLERS[el.dataset.action];
        if (handler) handler(el, e);
    });

    // KEYDOWN sobre los tabs — mismo alcance genérico que el click de arriba.
    // Sin esto el sidebar solo se opera con ratón: los tabs son <div>, así que
    // ni Enter ni las flechas hacen nada por sí solos.
    document.addEventListener('keydown', e => {
        const tab = e.target.closest?.('.modal-tab[data-modal]');
        if (!tab) return;
        if (TabbedModal.manejarTeclaEnTab(tab, e.key)) e.preventDefault();
    });

    // CHANGE delegation
    document.addEventListener('change', e => {
        const el = e.target.closest('[data-action]');
        if (!el) return;
        const handler = CHANGE_HANDLERS[el.dataset.action];
        if (handler) handler(el, e);
    });

    // INPUT delegation
    document.addEventListener('input', e => {
        const el = e.target.closest('[data-action]');
        if (!el) return;
        const handler = INPUT_HANDLERS[el.dataset.action];
        if (handler) handler(el, e);
    });

    // KEYUP delegation
    document.addEventListener('keyup', e => {
        const el = e.target.closest('[data-action]');
        if (!el) return;
        const handler = KEYUP_HANDLERS[el.dataset.action];
        if (handler) handler(el, e);
    });

    // SUBMIT delegation — form#formNodo usa data-action="nodos:save"
    document.addEventListener('submit', e => {
        const el = e.target.closest('[data-action]');
        if (!el) return;
        e.preventDefault();
        const handler = CLICK_HANDLERS[el.dataset.action];
        if (handler) handler(el, e);
    });

    // Prevenir que Enter en inputs de texto dispare submit prematuro en modales
    document.addEventListener('keydown', e => {
        // Accesibilidad: elementos con role=button + data-action se activan con
        // Enter/Espacio (estándar WAI-ARIA), igual que un botón nativo. Cubre los
        // headers de grupo del árbol y cualquier control accesible futuro.
        if (
            (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') &&
            e.target.matches?.('[role="button"][data-action]')
        ) {
            const handler = CLICK_HANDLERS[e.target.dataset.action];
            if (handler) {
                e.preventDefault();
                handler(e.target, e);
                return;
            }
        }
        if (e.key !== 'Enter') return;
        const tag = e.target.tagName;
        if (tag === 'TEXTAREA' || tag === 'BUTTON') return;
        if (e.target.closest('form[data-action]')) {
            e.preventDefault();
        }
    });

    // FORM SUBMIT — loginForm (submit nativo + Enter explícito como safety net)
    const loginForm = document.getElementById('loginForm');
    if (loginForm) {
        loginForm.addEventListener('submit', e => App.handleLogin(e));
        loginForm.addEventListener('keydown', e => {
            if (e.key === 'Enter') {
                e.preventDefault();
                loginForm.requestSubmit();
            }
        });
    }

    // "¿Olvidaste tu contraseña?" — abre el flujo de recuperación por código al
    // correo registrado (onboarding universal), con el email ya tipeado si lo hay.
    const loginOlvido = document.getElementById('loginOlvido');
    if (loginOlvido) {
        loginOlvido.addEventListener('click', () => {
            const email = (document.getElementById('loginEmail')?.value || '').trim();
            App.mostrarRecuperacionCuenta(email, false);
        });
    }
});
